import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FaEthereum } from 'react-icons/fa';
import { passportInstance } from '../../config/passport';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../config/api';

/**
 * NFTPurchaseButton Component
 * 
 * This component provides a button for users to purchase and mint NFTs using Immutable Passport.
 * It implements lazy minting - the NFT is only minted when a user purchases it.
 */
export default function NFTPurchaseButton({ nft, onSuccess, className = '' }) {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isPassportConnected, setIsPassportConnected] = useState(false);
  const { user, updateUserContext, isPassportUser } = useAuth();

  // Check if already connected with Passport
  useEffect(() => {
    if (user?.isConnected && isPassportUser()) {
      setIsPassportConnected(true);
    } else {
      setIsPassportConnected(false);
    }
  }, [user, isPassportUser]);

  const connectPassport = async () => {
    try {
      console.log('Connecting to Immutable Passport...');
      
      // First, check if user is already logged in with Passport
      try {
        const userInfo = await passportInstance.getUserInfo();
        console.log('User already logged in with Passport:', userInfo);
        
        // If we have user info but no provider, connect the provider
        if (userInfo) {
          // Connect to Passport EVM provider
          const provider = await passportInstance.connectEvm();
          console.log('Connected to Passport EVM provider:', provider);
          
          // Get user's address
          const accounts = await provider.request({ method: 'eth_requestAccounts' });
          const address = accounts[0];
          console.log('Got wallet address from Passport:', address);
          
          // Update auth context with Passport user info
          updateUserContext({
            ...user,
            passportAddress: address,
            passportInfo: userInfo
          });
          
          setIsPassportConnected(true);
          
          return { provider, address, userInfo };
        }
      } catch (userInfoError) {
        console.log('User not logged in with Passport, proceeding with login flow');
      }
      
      // Connect to Passport
      const provider = await passportInstance.connectEvm();
      console.log('Connected to Passport EVM provider:', provider);
      
      // Get user's address
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      const address = accounts[0];
      console.log('Got wallet address from Passport:', address);
      
      // Get user info
      const userInfo = await passportInstance.getUserInfo();
      console.log('Got user info from Passport:', userInfo);
      
      // Update auth context with Passport user info
      updateUserContext({
        ...user,
        passportAddress: address,
        passportInfo: userInfo
      });
      
      setIsPassportConnected(true);
      
      return { provider, address, userInfo };
    } catch (error) {
      console.error('Error connecting to Passport:', error);
      if (error.message) console.error('Error message:', error.message);
      if (error.stack) console.error('Error stack:', error.stack);
      toast.error('Failed to connect to Immutable Passport');
      throw error;
    }
  };

  const handlePurchase = async () => {
    if (isPurchasing) return;
    
    setIsPurchasing(true);
    const loadingToastId = toast.loading('Preparing to purchase NFT...');
    
    try {
      // Connect to Passport if not already connected
      let address = user?.passportAddress;
      
      if (!isPassportConnected) {
        const connection = await connectPassport();
        address = connection.address;
        toast.loading('Connected to Immutable Passport', { id: loadingToastId });
      }
      
      if (!address) {
        throw new Error('Failed to get wallet address');
      }
      
      // Call the purchase endpoint
      toast.loading('Processing your purchase...', { id: loadingToastId });
      
      const purchaseResponse = await api.post(API_ENDPOINTS.nft.purchase, {
        nftId: nft._id,
        buyerAddress: address
      });
      
      console.log('Purchase response:', purchaseResponse);
      
      toast.success('Purchase initiated! Your NFT is being minted.', { id: loadingToastId });
      
      // Poll for minting status
      pollMintStatus(nft._id, loadingToastId);
      
    } catch (error) {
      console.error('Error purchasing NFT:', error);
      toast.error(`Failed to purchase NFT: ${error.message || 'Unknown error'}`, { id: loadingToastId });
      setIsPurchasing(false);
    }
  };
  
  // Poll for minting status
  const pollMintStatus = async (nftId, toastId) => {
    try {
      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = 5000; // 5 seconds
      
      const checkStatus = async () => {
        attempts++;
        
        try {
          // Get the latest NFT data
          const response = await api.get(API_ENDPOINTS.nft.nft(nftId));
          const updatedNft = response.data;
          
          if (updatedNft.status === 'minted') {
            // Minting successful
            toast.success('NFT purchased and minted successfully!', { id: toastId });
            setIsPurchasing(false);
            if (onSuccess) onSuccess(updatedNft);
            return;
          } else if (updatedNft.status === 'draft') {
            // Minting failed
            toast.error('NFT purchase failed. Please try again.', { id: toastId });
            setIsPurchasing(false);
            return;
          } else if (attempts < maxAttempts) {
            // Still minting, check again
            toast.loading(`Minting in progress (${attempts}/${maxAttempts})...`, { id: toastId });
            setTimeout(checkStatus, pollInterval);
          } else {
            // Timeout
            toast.loading('Minting is taking longer than expected. Please check back later.', { id: toastId });
            setIsPurchasing(false);
          }
        } catch (error) {
          console.error('Error checking mint status:', error);
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, pollInterval);
          } else {
            toast.error('Failed to check mint status', { id: toastId });
            setIsPurchasing(false);
          }
        }
      };
      
      // Start checking
      setTimeout(checkStatus, 2000); // Initial delay
      
    } catch (error) {
      console.error('Error in pollMintStatus:', error);
      toast.error('Failed to check mint status', { id: toastId });
      setIsPurchasing(false);
    }
  };

  return (
    <button
      onClick={handlePurchase}
      disabled={isPurchasing}
      className={`flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:from-violet-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {isPurchasing ? (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
          Processing...
        </>
      ) : (
        <>
          <FaEthereum className="mr-2" />
          {isPassportConnected ? 'Buy Now' : 'Connect & Buy'}
        </>
      )}
    </button>
  );
}
